// Packages
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import defaultImage from "../../../assets/images/checkout/150x150.png";
import { useTranslation } from "react-i18next";
import { Handbag } from "@phosphor-icons/react";

// Components
import LoadingIcon from "./LoadingIcon";
import OrderSummary from "./OrderSummary";

// Actions
import { addPromotionCoupon, fetchDataPayment } from "../actions/checkoutStore";

const SummaryForm = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { summary, information, urls, colors } = useSelector(
    (state) => state.checkoutStore,
  );

  const { isLoading, doingRequest } = useSelector((state) => state.ui);

  const [couponField, setCouponField] = useState("");

  const [overflowingItems, setOverflowingItems] = useState({});

  const textRefs = useRef({});
  const containerRefs = useRef({});

  useEffect(() => {
    const checkOverflow = () => {
      if (!summary.products) return;

      const newOverflowingItems = {};
      const getMaxLength = (width) => Math.round(0.143 * width - 13.69);
      const maxLength =
        window.innerWidth > 991 ? 40 : getMaxLength(window.innerWidth);

      summary.products.forEach((product) => {
        newOverflowingItems[product.id] = product.name.length > maxLength;
      });

      setOverflowingItems(newOverflowingItems);
    };

    checkOverflow();
  }, []);

  const goToCart = () => {
    window.location = urls.cart;
  };

  const handleResize = () => {
    const getTotalHeight = (className) => {
      const element = document.getElementsByClassName(className)[0];
      if (!element) return 0;

      const style = window.getComputedStyle(element);

      const marginTop = parseFloat(style.marginTop) || 0;
      const marginBottom = parseFloat(style.marginBottom) || 0;
      return element.offsetHeight + marginTop + marginBottom;
    };

    const summaryDivHeight = getTotalHeight("summary-div");
    const discountDivHeight = getTotalHeight("discount-div");
    const summaryHeaderDivHeight = getTotalHeight("summary-header");
    let productList = document.getElementsByClassName(
      "scrollable-container",
    )[0];
    if (productList == null) return;
    productList.style.maxHeight = `calc(100vh - ${summaryDivHeight + discountDivHeight + summaryHeaderDivHeight + 48}px)`;
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [summary]);

  const addPromotion = () => {
    if (couponField == "") {
      return;
    }
    setCouponField("");
    dispatch(addPromotionCoupon(couponField, information.email));
    dispatch(fetchDataPayment());
  };

  const displayProduct = (product) => {
    return (
      <div
        className="d-flex mb-3"
        style={{ maxWidth: "100%" }}
        key={product.id}
      >
        <div>
          <img
            width={44}
            height={44}
            src={product.img || defaultImage}
            alt="product thumb image"
            style={{ objectFit: "cover" }}
          />
        </div>
        <div
          key={product.id}
          className="cart-product-name-div ps-2 d-flex flex-column justify-content-between text-truncate"
          ref={(el) => (containerRefs.current[product.id] = el)}
          style={{
            height: "44px",
            flexGrow: 1,
            overflow: "hidden",
            position: "relative",
            whiteSpace: "nowrap",
          }}
        >
          <p
            className={`m-0 fw-bold cart-product-text${window.innerWidth > 991 ? "" : "-mobile"} ${
              overflowingItems[product.id] ? "moving" : ""
            }`}
            ref={(el) => (textRefs.current[product.id] = el)}
          >
            {product.name}{" "}
            {product.subscription_period > 0 && t("summary.subscription")}
          </p>

          <div className="d-flex justify-content-between">
            <div>
              <p className="m-0">
                {t("summary.qty")} {product.qty}{" "}
                {product.subscription_label &&
                  t("summary.subscription_every", {
                    period: product.subscription_label,
                  })}
              </p>
            </div>

            <div>
              <p className="m-0">
                {product.price != product.discount && <s>{product.price} </s>}
                {product.discount}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const displayProductCount = () => {
    const productCount = summary.products.reduce(
      (n, item) => (item.qty ? n + item.qty : n),
      0,
    );
    return (
      <div className="ps-1 span-font-14 fw-semibold">
        {productCount == 1
          ? t("summary.item")
          : t("summary.items", { items: productCount })}
      </div>
    );
  };

  const SummaryHeader = (
    <div>
      {!props.is_mobile && (
        <div className="summary-header pb-0 pt-0 mt-4 mb-3">
          <h6 className="d-inline-flex">
            <Handbag /> {displayProductCount()}
          </h6>
          <button
            id="edit-cart"
            className="border-0 btn shadow-none px-0 pr-0 underline-link"
            style={{ right: "-12px", bottom: "0px" }}
            disabled={doingRequest}
            onClick={goToCart}
          >
            <p className="mb-0">{t("summary.edit-cart")}</p>
          </button>
        </div>
      )}
      <div className="pt-0 summary-products position-relative">
        <div
          className="scrollable-container"
          style={{
            overflowY: "auto",
          }}
          onScroll={(e) => {
            const scrollableContainer = e.currentTarget;
            if (scrollableContainer.scrollTop == 0) {
              scrollableContainer.classList.remove("border-top");
            } else if (
              scrollableContainer.scrollTop >=
              scrollableContainer.scrollHeight -
                scrollableContainer.clientHeight
            ) {
              scrollableContainer.classList.remove("border-bottom");
            } else {
              scrollableContainer.classList.add("border-top");
              scrollableContainer.classList.add("border-bottom");
            }
          }}
        >
          {summary.products.map((p) => displayProduct(p))}
        </div>
        {props.is_mobile && (
          <div className="d-flex justify-content-center mb-1">
            <button
              id="edit-cart"
              className="border-0 btn shadow-none px-0 pr-0 underline-link"
              style={{ right: "-12px", bottom: "0px" }}
              disabled={doingRequest}
              onClick={goToCart}
            >
              <p className="mb-0">{t("summary.edit-cart")}</p>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const onKeyUp = (e) => {
    if (e.charCode === 13 && !doingRequest) {
      addPromotion();
    }
  };

  const Discount = (
    <div className="discount-div mt-4">
      <div className="">
        <label htmlFor="discount" className="form-label">
          {t("summary.discount-code")}
        </label>
        <div className="discount-content">
          <input
            type="text"
            className="form-control floating-label-field--s1 flex-grow-1"
            id="discount"
            disabled={doingRequest}
            placeholder="Discount Code"
            value={couponField}
            onChange={(e) => setCouponField(e.target.value)}
            onKeyPress={onKeyUp}
            style={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              backgroundColor: "#FFFFFF",
            }}
          />
          <button
            className="btn summary-button summary-add-btn text-nowrap"
            id="add-promo-button"
            disabled={doingRequest || couponField === ""}
            onClick={addPromotion}
            style={{ color: "#505050" }}
          >
            {t("summary.apply")}
          </button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <LoadingIcon default />;
  }

  return (
    <div
      className={`summary summary-collapsible mb-0 mx-3 mx-lg-0 pe-lg-4 position-sticky sticky-top`}
      style={{
        width: information.is_mobile ? "calc(100% - 32px)" : "100%",
        paddingTop: props.is_mobile ? "0px" : "48px",
        zIndex: 1,
      }}
    >
      <div className="">{SummaryHeader}</div>

      {summary.has_promotions && !props.is_mobile && (
        <div className="discount-div mt-4">{Discount}</div>
      )}
      {!props.is_mobile && (
        <div className="summary-div mt-4 mb-3">
          <div className="">
            <OrderSummary />
          </div>
          {props.navigationFooter}
        </div>
      )}
    </div>
  );
};

export default SummaryForm;
